import { ref, listAll, getMetadata } from 'firebase/storage';
import { originalPrefix, type OriginalBlobMetadata } from '#types';
import { storage } from '../services/startStorage';

export interface UserTempFileMeta {
  /** The upload UUID — the durable, short id for this file (the folder's `name`). */
  fileId: string;
  /** `temp/<userId>/<fileFolderId>` — the per-file folder. */
  fileFolder: string;
  /** Full GCS path of the original blob (`<fileFolder>/original.<ext>`). */
  originalPath: string;
  /** Display filename from the original blob's `customMetadata.displayName`. */
  filename: string;
  sizeBytes: number;
  contentType?: string;
  uploadedAt: string;
}

export async function listUserTempFiles(userId: string): Promise<UserTempFileMeta[]> {
  const userRef = ref(storage, `temp/${userId}`);
  const userListing = await listAll(userRef);
  const metas: (UserTempFileMeta | null)[] = await Promise.all(
    userListing.prefixes.map(async (folderRef): Promise<UserTempFileMeta | null> => {
      const folderListing = await listAll(folderRef);
      const prefix = originalPrefix(folderRef.fullPath);
      const originalItems = folderListing.items.filter((item) => item.fullPath.startsWith(prefix));
      if (originalItems.length !== 1) return null;
      const originalItem = originalItems[0];
      const meta = await getMetadata(originalItem);
      const custom = meta.customMetadata as Partial<OriginalBlobMetadata> | undefined;
      const result: UserTempFileMeta = {
        fileId: folderRef.name,
        fileFolder: folderRef.fullPath,
        originalPath: originalItem.fullPath,
        filename: custom?.displayName ?? originalItem.name,
        sizeBytes: meta.size,
        ...(meta.contentType ? { contentType: meta.contentType } : {}),
        uploadedAt: custom?.uploadedAt ?? meta.updated,
      };
      return result;
    }),
  );
  return metas
    .filter((m): m is UserTempFileMeta => m !== null)
    .sort((a, b) => b.uploadedAt.localeCompare(a.uploadedAt));
}
